import { useMutation } from '@apollo/client';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/useAuth';
import { ROUTES } from '@/constants/routes';
import { useToast } from '@/hooks/useToast';

import { BECOME_CANDIDATE } from '../graphql/mutations';

/**
 * Runs the Candidate opt-in end to end: `becomeCandidate` (TalentProfile + role in one backend
 * transaction), then a session refresh — the `Candidate` role is carried in the access token, so
 * without the refresh the new workspace would stay invisible until a reload — then straight into
 * the Candidate workspace.
 */
export function useBecomeCandidate() {
  const [becomeCandidateM, { loading }] = useMutation(BECOME_CANDIDATE);
  const { refreshSession } = useAuth();
  const [refreshing, setRefreshing] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  const becomeCandidate = useCallback(async () => {
    const res = await becomeCandidateM();
    const errors = res.data?.becomeCandidate.errors ?? [];
    if (errors.length > 0) {
      toast.showError(errors[0].message);
      return false;
    }
    setRefreshing(true);
    try {
      await refreshSession();
    } catch {
      // The role WAS granted; a failed refresh only means the token is stale. A hard navigation
      // re-establishes the session with the new role instead of leaving the user stuck.
      window.location.assign(ROUTES.candidate);
      return true;
    } finally {
      setRefreshing(false);
    }
    navigate(ROUTES.candidate, { replace: true });
    return true;
  }, [becomeCandidateM, refreshSession, toast, navigate]);

  return { becomeCandidate, becoming: loading || refreshing };
}
